import { useCallback, useEffect, useRef } from 'react';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useApplicationType } from '../common/hooks/use-application-type';
import { useEventQueueViews } from '../event-queues/event-queue-views-provider';
import { useAudibleAlerts } from '../sounds/play-sound-provider';
import { useUnitTimersStore } from '../unit-timers/use-unit-timers-store';
import { getIncomingAssignmentEventPriority } from './incoming-assignment-event-priority';

/**
 * Sounds the incoming assignment alert when the modal opens, and again only if the assignment
 * switches between routine and AED. Returns a stable callback that stops it.
 */
export function useIncomingAssignmentAlert({
    eventId,
    isAed,
}: {
    eventId: number;
    isAed: boolean;
}): () => void {
    const unitTimerStore = useUnitTimersStore();
    const enabledFeatures = useEnabledFeatures();
    const { isCad } = useApplicationType();
    const { eventQueueViewsRef } = useEventQueueViews();
    const { playAlert, stopAlert } = useAudibleAlerts();
    const wasPlayedRef = useRef(false);
    // Read through refs, so a new eventId on the already-open modal, new preferences, or the event's
    // queue row or panel view arriving after the modal opens doesn't replay the alert.
    const eventIdRef = useRef(eventId);
    eventIdRef.current = eventId;
    const alertContextRef = useRef({ enabledFeatures, isCad });
    alertContextRef.current = { enabledFeatures, isCad };

    useEffect(
        function playIncomingAssignmentAlert() {
            const currentEventId = eventIdRef.current;
            wasPlayedRef.current = playAlert({
                type: 'INCOMING_ASSIGNMENT',
                context: alertContextRef.current,
                isAed,
                ...getIncomingAssignmentEventPriority(
                    currentEventId,
                    eventQueueViewsRef.current[currentEventId]
                ),
            });
            if (wasPlayedRef.current) {
                // Or else the unit timer alert would play over it.
                unitTimerStore.disablePlaySound();
            }
        },
        [isAed, unitTimerStore, playAlert, eventQueueViewsRef]
    );

    return useCallback(
        function stopIncomingAssignmentAlert() {
            if (wasPlayedRef.current) {
                unitTimerStore.enablePlaySound();
                stopAlert('INCOMING_ASSIGNMENT');
            }
        },
        [unitTimerStore, stopAlert]
    );
}
